import React, { createContext, useContext, useEffect } from "react";

import jwtDecode from "jwt-decode";

import { useAuth } from "../hooks/useAuth";
import { useActiveAccount } from "../accounts/hooks/useAccounts";

// This global is setup in index.html
declare global {
  var dataLayer: any[];
}

window.dataLayer = window.dataLayer || [];

function gtag(data: any) {
  window.dataLayer.push(data);
}

const GTagContext = createContext(gtag);

export default function GTagProvider({ children }) {
  return <GTagContext.Provider value={gtag}>{children}</GTagContext.Provider>;
}

export function useGtag() {
  return useContext(GTagContext);
}

export type GTagUserContext = {
  user_id: string | undefined;
};

// Note: Variables are persistent, and must be explicitly cleared when no longer valid.
// For this purpose, we issue reset messages to clear data on certain events.
const userContextReset: GTagUserContext = {
  user_id: undefined,
};

export type GTagAccountContext = {
  account_name: string | undefined;
  plan: string | undefined;
};

const accountContextReset: GTagAccountContext = {
  account_name: undefined,
  plan: undefined,
};

export function GTagUserTracker({}) {
  const gtag = useGtag();
  const { jwt } = useAuth();

  useEffect(() => {
    if (jwt) {
      if (jwt?.sub) {
        gtag({
          user_id: jwt.sub,
        });
      }

      // NB: The concerns are somewhat mixed here.
      // The account context tracker must render under an account-scoped route
      // to retrieve the active account information. This means it won't be
      // around to track sign outs. Here, we detect when the JWT has no org_id,
      // and then clear the account context.
      if (!jwt?.org_id) {
        gtag(accountContextReset);
      }
    } else {
      // If the user is not logged in at all, clear all user & account info.
      gtag({
        ...userContextReset,
        ...accountContextReset,
      });
    }
  }, [jwt]);

  return null;
}

export function GTagAccountTracker({}) {
  const gtag = useGtag();
  const { activeAccount, accountInfo } = useActiveAccount();

  useEffect(() => {
    if (activeAccount && accountInfo) {
      const context: GTagAccountContext = {
        account_name: activeAccount.name,
        plan: accountInfo.subscription?.plan_id,
      };
      gtag(context);
    }
  }, [activeAccount, accountInfo]);

  return null;
}
